// ==========================================
// src/lib/AuthContext.jsx — V17 DEFINITIVO
// ✅ CORRIGIDO: Busca nome_completo do motorista na tabela motoristas
// ✅ CORRIGIDO: buildUserData recebe motoristaData e prioriza nome correto
// ✅ CORRIGIDO: SIGNED_IN processado IMEDIATAMENTE
// ==========================================
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return context;
};

// ✅ CORRIGIDO: Recebe motoristaData e prioriza nome do motorista
const buildUserData = (authUser, perfil = null, cadastroCompleto = false, empresa = null, motorista = null) => {
  const safePerfil = perfil || {};
  return {
    ...safePerfil,
    id: authUser.id,
    email: authUser.email,
    // ✅ CORRIGIDO: Prioridade correta do nome
    // 1º: nome_completo da tabela motoristas
    // 2º: nome da tabela profiles
    // 3º: full_name do Google OAuth
    // 4º: prefixo do email
    nome: motorista?.nome_completo || safePerfil.nome || authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'Usuário',
    tipo: safePerfil.tipo || null,
    role: safePerfil.role || 'user',
    avatar: safePerfil.avatar_url || authUser.user_metadata?.avatar_url || null,
    cadastro_aprovado: safePerfil.cadastro_aprovado ?? false,
    status: safePerfil.status || 'em_analise',
    precisaCadastro: !safePerfil.tipo,
    cadastroCompleto,
    cnpj: empresa?.cnpj || safePerfil.cnpj || null,
    razao_social: empresa?.razao_social || safePerfil.razao_social || null,
    logo_url: empresa?.logo_url || safePerfil.logo_url || null,
    nome_fantasia: empresa?.nome_fantasia || safePerfil.nome_fantasia || null,
    // ✅ NOVO: Dados do motorista disponíveis no user
    motorista_cpf: motorista?.cpf || null,
    motorista_cnh: motorista?.cnh || null,
    motorista_placa: motorista?.placa_caminhao || null,
    motorista_telefone: motorista?.telefone || null,
    motorista_categoria: motorista?.categoria_cnh || null,
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const isCheckingAuth = useRef(false);
  const mountedRef = useRef(true);

  const checkAuth = useCallback(async (force = false) => {
    if (isCheckingAuth.current && !force) {
      console.log('⏳ checkAuth já em execução, ignorando...');
      return;
    }

    isCheckingAuth.current = true;

    try {
      // ✅ PRIMÁRIO: getSession() — mais confiável que getUser()
      let session = null;
      let attempts = 0;
      const maxAttempts = 3;

      while (!session && attempts < maxAttempts) {
        const { data: { session: s }, error: sessionError } = await supabase.auth.getSession();
        if (!sessionError && s?.user) {
          session = s;
          break;
        }
        attempts++;
        if (attempts < maxAttempts) {
          console.log(`⏳ Tentativa ${attempts} falhou, aguardando...`);
          await new Promise(r => setTimeout(r, 500 * attempts));
        }
      }

      // ✅ Se não achou com getSession, tenta refreshSession (último recurso)
      if (!session?.user) {
        console.log('🔄 Tentando refreshSession no checkAuth...');
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData?.session?.user) {
          session = refreshData.session;
          console.log('✅ Sessão recuperada via refresh!');
        }
      }

      if (!session?.user) {
        console.log('⚠️ Sem sessão ativa após todas tentativas');
        if (!mountedRef.current) return;
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      const authUser = session.user;
      console.log('✅ AuthContext - Sessão ativa:', authUser.id);

      let perfil = null;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries && !perfil) {
        try {
          const { data, error: perfilError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', authUser.id)
            .maybeSingle();

          if (perfilError) {
            console.warn('⚠️ Erro perfil:', perfilError);
            retryCount++;
            await new Promise(r => setTimeout(r, 300 * retryCount));
            continue;
          }
          
          perfil = data;
          break;
        } catch (err) {
          console.warn('⚠️ Exception perfil:', err);
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(r => setTimeout(r, 300 * retryCount));
          }
        }
      }

      let cadastroCompleto = false;
      let empresaData = null;
      let motoristaData = null;  // ✅ NOVO: guardar dados completos do motorista

      if (perfil?.tipo === 'motorista') {
        // ✅ CORRIGIDO: Busca nome_completo e mais dados do motorista
        const { data: motorista } = await supabase
          .from('motoristas')
          .select('nome_completo, cpf, cnh, placa_caminhao, telefone, email, categoria_cnh')
          .eq('usuario_id', authUser.id)
          .maybeSingle();
        
        motoristaData = motorista;  // ✅ NOVO: salvar motoristaData
        cadastroCompleto = !!(motorista?.cpf && motorista?.cnh && motorista?.placa_caminhao);
      }

      if (perfil?.tipo === 'empresa') {
        const { data: empresa } = await supabase
          .from('empresas')
          .select('cnpj, razao_social, nome_fantasia, logo_url, status')
          .eq('usuario_id', authUser.id)
          .maybeSingle();
        cadastroCompleto = !!(empresa?.cnpj);
        empresaData = empresa;
      }

      if (!mountedRef.current) return;

      // ✅ CORRIGIDO: Passar motoristaData para buildUserData
      const userData = buildUserData(authUser, perfil, cadastroCompleto, empresaData, motoristaData);
      const isUserAdmin = perfil?.role === 'admin';
      
      console.log('✅ AuthContext - User carregado:', userData.nome, '| Tipo:', userData.tipo);
      
      setIsAdmin(isUserAdmin);
      setUser(userData);
      setIsAuthenticated(true);

    } catch (err) {
      console.error('❌ AuthContext - Erro:', err);
      if (!mountedRef.current) return;
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
      isCheckingAuth.current = false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('📡 Auth event:', event, '- User:', session?.user?.id || 'null');

      if (event === 'INITIAL_SESSION') {
        console.log('✅ Processando INITIAL_SESSION');
        if (session?.user) {
          setTimeout(() => checkAuth(true), 100);
        } else {
          console.log('⚠️ INITIAL_SESSION sem user, ignorando');
        }
        return;
      }

      // ✅ CORREÇÃO: SIGNED_IN processado IMEDIATAMENTE
      if (event === 'SIGNED_IN') {
        console.log('✅ Usuário logado via SIGNED_IN');
        setTimeout(() => checkAuth(true), 500);
        return;
      }

      if (event === 'SIGNED_OUT') {
        console.log('👋 Processando SIGNED_OUT');
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        setIsLoading(false);
      }

      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔑 Modo recovery');
      }

      if (event === 'USER_UPDATED') {
        console.log('📝 Usuário atualizado');
        setTimeout(() => checkAuth(true), 100);
      }
      
      if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token renovado');
        setTimeout(() => checkAuth(true), 100);
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });
      if (error) {
        setIsLoading(false);
        return { sucesso: false, error: error.message };
      }
      return { sucesso: true };
    } catch (err) {
      setIsLoading(false);
      return { sucesso: false, error: 'Erro ao fazer login' };
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      localStorage.removeItem('borafrete_contratos');
      localStorage.removeItem('borafrete_cargas');
      localStorage.removeItem('borafrete_arquivos');
      await supabase.auth.signOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      return { sucesso: true };
    } catch (err) {
      return { sucesso: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) throw error;
      return { sucesso: true };
    } catch (err) {
      return { sucesso: false, error: err.message };
    }
  };

  const refreshUser = useCallback(async () => {
    await checkAuth(true);
  }, [checkAuth]);

  const value = {
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    login,
    logout,
    loginWithGoogle,
    checkAuth,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}